export enum MigrationType {
  SCHEMA = 'schema',
  CODE = 'code', 
  STRUCTURE = 'structure',
  DEPENDENCY = 'dependency',
  BREAKING = 'breaking',
  TRANSFORMATION = 'transformation'
}

export interface ProjectStructure {
  path: string;
  version: string;
  packageJson: any;
  dezzyConfig?: any;
  files: ProjectFile[];
  directories: string[];
}

export interface ProjectFile {
  path: string;
  content: string;
  type: 'typescript' | 'javascript' | 'json' | 'markdown' | 'css' | 'other';
}

export interface Migration {
  id: string;
  version: string;
  type: MigrationType;
  description: string;
  breaking: boolean;
  transform: (project: ProjectStructure) => Promise<MigrationResult>;
  validate?: (project: ProjectStructure) => Promise<ValidationResult>;
  rollback?: (project: ProjectStructure, backup: BackupData) => Promise<void>;
  estimateTime?: (project: ProjectStructure) => number; // milliseconds
}

export interface MigrationResult {
  success: boolean;
  changes: FileChange[];
  errors?: string[];
  warnings?: string[];
  data?: any;
}

export interface ValidationResult {
  valid: boolean;
  errors?: string[];
  warnings?: string[];
  suggestions?: string[];
}

export interface FileChange {
  path: string;
  type: 'create' | 'update' | 'delete' | 'move';
  oldContent?: string;
  newContent?: string;
  oldPath?: string; // for move operations
}

export interface BackupData {
  id: string;
  timestamp: Date;
  projectPath: string;
  backupPath: string;
  version: string;
  files: Array<{
    path: string;
    content: string;
  }>;
  metadata: {
    migrationId?: string;
    step?: number;
    totalSteps?: number;
    emergency?: boolean;
    migrationPlan?: MigrationPlan;
  };
}

export interface MigrationPlan {
  fromVersion: string;
  toVersion: string;
  migrations: Migration[];
  estimatedTime: number;
  breakingChanges: boolean;
  dependencies: {
    add: string[];
    remove: string[];
    update: Array<{ name: string; from: string; to: string }>;
  };
}

export interface MigrationReport {
  success: boolean;
  plan?: MigrationPlan; // Optional for auto-migrations
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  completedMigrations?: string[]; // Legacy field
  migrationsApplied?: string[]; // New field for auto-migrations
  failedMigration?: {
    id: string;
    error: string;
    step: number;
  };
  backup?: BackupData;
  changes?: FileChange[];
  rollbackAvailable?: boolean;
  targetVersion?: string; // New field for auto-migrations
  error?: string; // New field for auto-migrations
}

export interface MigrationOptions {
  target?: string;
  dryRun?: boolean;
  skipBackup?: boolean;
  interactive?: boolean;
  force?: boolean;
  verbose?: boolean;
}

export interface VersionDetector {
  detectVersion(projectPath: string): Promise<string | null>;
  validateProject(projectPath: string): Promise<ValidationResult>;
}

export interface MigrationEngine {
  detectVersion(projectPath: string): Promise<string>;
  planMigration(fromVersion: string, toVersion: string): Promise<MigrationPlan>;
  executeMigration(plan: MigrationPlan, projectPath: string, options?: MigrationOptions): Promise<MigrationReport>;
  rollback(projectPath: string, backupId?: string): Promise<boolean>;
  listBackups(projectPath: string): Promise<BackupData[]>;
  cleanupBackups(projectPath: string, keepCount?: number): Promise<void>;
}

export interface BackupManager {
  create(projectPath: string, metadata?: any): Promise<BackupData>;
  restore(backup: BackupData, targetPath: string): Promise<void>;
  list(projectPath: string): Promise<BackupData[]>;
  delete(backupId: string): Promise<void>;
  cleanup(projectPath: string, keepCount: number): Promise<void>;
}

export interface DependencyManager {
  analyze(packageJsonPath: string): Promise<DependencyAnalysis>;
  update(packageJsonPath: string, changes: DependencyChanges): Promise<void>;
  install(projectPath: string, packageManager?: 'npm' | 'yarn' | 'pnpm'): Promise<void>;
}

export interface DependencyAnalysis {
  current: Record<string, string>;
  outdated: Array<{
    name: string;
    current: string;
    latest: string;
    breaking: boolean;
  }>;
  missing: string[];
  conflicts: Array<{
    name: string;
    requiredBy: string[];
    versions: string[];
  }>;
}

export interface DependencyChanges {
  add?: Record<string, string>;
  remove?: string[];
  update?: Record<string, string>;
  addDev?: Record<string, string>;
  removeDev?: string[];
  updateDev?: Record<string, string>;
}

// Schema migration specific types
export interface SectionManifestV1 {
  id: string;
  name: string;
  description: string;
  version: string;
  variants: Array<{
    name: string;
    description: string;
    presentation?: boolean;
    props?: Record<string, any>;
    slots?: string[];
  }>;
}

export interface SectionManifestV2 extends Omit<SectionManifestV1, 'variants'> {
  category: 'element' | 'component' | 'section';
  props?: Record<string, {
    type: string;
    required?: boolean;
    default?: any;
    description?: string;
  }>;
  defaultProps?: Record<string, any>;
  variants: Array<{
    name: string;
    description?: string;
    presentation?: boolean;
    props?: Record<string, any>;
    tokens?: Record<string, string>;
    slots?: string[];
    conditions?: Array<{
      prop: string;
      operator: 'equals' | 'contains' | 'greater' | 'less';
      value: any;
    }>;
  }>;
  schema?: {
    type: string;
    properties?: Record<string, any>;
    required?: string[];
  };
  tokens?: Record<string, string>;
  dependencies?: string[];
  accessibility?: {
    role?: string;
    ariaLabel?: string;
    keyboardSupport?: boolean;
  };
  stories?: Array<{
    name: string;
    args: Record<string, any>;
  }>;
  examples?: Array<{
    name: string;
    dsl: any; // ViewDSL
    description?: string;
  }>;
}

export interface ConfigMigration {
  transformPackageJson(packageJson: any): any;
  transformDezzyConfig(config: any): any;
  addFiles(): Array<{ path: string; content: string }>;
  removeFiles(): string[];
  moveFiles(): Array<{ from: string; to: string }>;
}

export interface CodeTransformer {
  transformImports(content: string, mapping: Record<string, string>): string;
  transformAPICalls(content: string, transformations: APITransformation[]): string;
  transformTypeDefinitions(content: string, typeChanges: TypeChange[]): string;
}

export interface APITransformation {
  from: {
    module?: string;
    function: string;
    signature: string;
  };
  to: {
    module?: string;
    function: string;
    signature: string;
  };
  notes?: string;
}

export interface TypeChange {
  from: string;
  to: string;
  module?: string;
  breaking: boolean;
}

export interface MigrationRegistry {
  register(migration: Migration): void;
  get(id: string): Migration | undefined;
  getByVersion(version: string): Migration[];
  getPath(fromVersion: string, toVersion: string): Migration[];
  list(): Migration[];
}

export interface ProjectAnalyzer {
  analyzeStructure(projectPath: string): Promise<ProjectStructure>;
  findDezzyProjects(rootPath: string): Promise<string[]>;
  validateDezzyProject(projectPath: string): Promise<ValidationResult>;
  extractMetadata(projectPath: string): Promise<ProjectMetadata>;
}

export interface ProjectMetadata {
  name: string;
  version: string;
  dezzyVersion: string;
  template?: string;
  features: string[];
  dependencies: Record<string, string>;
  lastMigration?: {
    id: string;
    version: string;
    timestamp: Date;
  };
}

// Error types
export class MigrationError extends Error {
  constructor(
    message: string,
    public migrationId: string,
    public step?: number,
    public rollbackAvailable: boolean = true
  ) {
    super(message);
    this.name = 'MigrationError';
  }
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public issues: string[],
    public suggestions?: string[]
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class BackupError extends Error {
  constructor(
    message: string,
    public operation: 'create' | 'restore' | 'delete' | 'list',
    public backupId?: string
  ) {
    super(message);
    this.name = 'BackupError';
  }
}